/**
 * Batch content optimization applied at render time.
 *
 * Per-batch in-body fixes (see CONTENT_OPTIMIZATION_GUIDE.md):
 *   1. CTA migration: `/#contact` → `/book` (low-friction booking page)
 *   2. Answer-first restructure: promotes the true answer to the opening
 *      paragraph for AEO/LLM retrieval.
 *   3. Keyword de-stuffing: unwraps the bolded exact-slug phrase in the first
 *      paragraph so the lead reads naturally.
 *   4. Information gain: injects a decision framework / checklist asset where
 *      the article benefits from one.
 *   5. Homepage contextual link for hiring topics (per INTERNAL_LINKING_GUIDE).
 *   6. Stacked-lead de-clustering: rewrites the formulaic "If you want to [A],
 *      or you want [B], or you want [C]" tail (Batches 34-51) so the home link
 *      moves to paragraph 2, the service link(s) read as one comparative
 *      sentence, and the skeleton varies per article (no verbatim repetition).
 *
 * The per-batch slug/lead/info-gain data lives in each batch*Content.ts module
 * and is aggregated into an ordered dispatch table in batchDispatch.ts. This
 * file keeps only the transform logic + the data-driven loop. Batch 23
 * (original) is the lone special case (asset only, before the second H2).
 */
import { BATCH23_INFO_GAIN } from "@/lib/batch23Assets";
import { BATCH_DISPATCH } from "@/lib/batchDispatch";
import { BATCH29_HIRING_SLUGS } from "@/lib/batch29Content";
import {
  BATCH30_HIRING_SLUGS,
  BATCH31_HIRING_SLUGS,
} from "@/lib/batchFinalContent";
import { BATCH32_HIRING_SLUGS } from "@/lib/batch32Content";
import { BATCH33_HIRING_SLUGS } from "@/lib/batch33Content";
import { BATCH34_HIRING_SLUGS } from "@/lib/batch34Content";
import { BATCH35_HIRING_SLUGS } from "@/lib/batch35Content";
import { BATCH36_HIRING_SLUGS } from "@/lib/batch36Content";
import { BATCH37_HIRING_SLUGS } from "@/lib/batch37Content";
import { BATCH38_HIRING_SLUGS } from "@/lib/batch38Content";
import { BATCH39_HIRING_SLUGS } from "@/lib/batch39Content";
import { BATCH40_HIRING_SLUGS } from "@/lib/batch40Content";
import { BATCH41_HIRING_SLUGS } from "@/lib/batch41Content";
import { BATCH42_HIRING_SLUGS } from "@/lib/batch42Content";
import { BATCH43_HIRING_SLUGS } from "@/lib/batch43Content";
import { BATCH44_HIRING_SLUGS } from "@/lib/batch44Content";
import { BATCH45_HIRING_SLUGS } from "@/lib/batch45Content";
import { BATCH46_HIRING_SLUGS } from "@/lib/batch46Content";
import { BATCH47_HIRING_SLUGS } from "@/lib/batch47Content";
import { BATCH48_HIRING_SLUGS } from "@/lib/batch48Content";
import { BATCH49_HIRING_SLUGS } from "@/lib/batch49Content";
import { BATCH50_HIRING_SLUGS } from "@/lib/batch50Content";
import { BATCH51_HIRING_SLUGS } from "@/lib/batch51Content";
import { BATCH52_HIRING_SLUGS } from "@/lib/batch52Content";
import { BATCH53_HIRING_SLUGS } from "@/lib/batch53Content";
import { BATCH54_HIRING_SLUGS } from "@/lib/batch54Content";
import { BATCH55_HIRING_SLUGS } from "@/lib/batch55Content";
import { BATCH56_HIRING_SLUGS } from "@/lib/batch56Content";
import { BATCH57_HIRING_SLUGS } from "@/lib/batch57Content";
import { BATCH58_HIRING_SLUGS } from "@/lib/batch58Content";
import { BATCH59_HIRING_SLUGS } from "@/lib/batch59Content";
import { BATCH60_HIRING_SLUGS } from "@/lib/batch60Content";
import { BATCH61_HIRING_SLUGS } from "@/lib/batch61Content";
import { BATCH62_HIRING_SLUGS } from "@/lib/batch62Content";
import { BATCH63_HIRING_SLUGS } from "@/lib/batch63Content";
import { BATCH64_HIRING_SLUGS } from "@/lib/batch64Content";
import { BATCH65_HIRING_SLUGS } from "@/lib/batch65Content";
import { BATCH66_HIRING_SLUGS } from "@/lib/batch66Content";
import { BATCH67_HIRING_SLUGS } from "@/lib/batch67Content";
import { BATCH68_HIRING_SLUGS } from "@/lib/batch68Content";
import { BATCH69_HIRING_SLUGS } from "@/lib/batch69Content";
import { BATCH70_HIRING_SLUGS } from "@/lib/batch70Content";
import { BATCH71_HIRING_SLUGS } from "@/lib/batch71Content";
import { BATCH72_HIRING_SLUGS } from "@/lib/batch72Content";
import { BATCH73_HIRING_SLUGS } from "@/lib/batch73Content";
import { BATCH74_HIRING_SLUGS } from "@/lib/batch74Content";
import { BATCH75_HIRING_SLUGS } from "@/lib/batch75Content";
import { BATCH23_SLUGS, HIRING_TOPIC_SLUGS } from "@/lib/batchSlugs";

/** Combined hiring-topic set: base + Batch 29–51 hiring slugs. */
const ALL_HIRING_SLUGS = new Set<string>([
  ...HIRING_TOPIC_SLUGS,
  ...BATCH29_HIRING_SLUGS,
  ...BATCH30_HIRING_SLUGS,
  ...BATCH31_HIRING_SLUGS,
  ...BATCH32_HIRING_SLUGS,
  ...BATCH33_HIRING_SLUGS,
  ...BATCH34_HIRING_SLUGS,
  ...BATCH35_HIRING_SLUGS,
  ...BATCH36_HIRING_SLUGS,
  ...BATCH37_HIRING_SLUGS,
  ...BATCH38_HIRING_SLUGS,
  ...BATCH39_HIRING_SLUGS,
  ...BATCH40_HIRING_SLUGS,
  ...BATCH41_HIRING_SLUGS,
  ...BATCH42_HIRING_SLUGS,
  ...BATCH43_HIRING_SLUGS,
  ...BATCH44_HIRING_SLUGS,
  ...BATCH45_HIRING_SLUGS,
  ...BATCH46_HIRING_SLUGS,
  ...BATCH47_HIRING_SLUGS,
  ...BATCH48_HIRING_SLUGS,
  ...BATCH49_HIRING_SLUGS,
  ...BATCH50_HIRING_SLUGS,
  ...BATCH51_HIRING_SLUGS,
  ...BATCH52_HIRING_SLUGS,
  ...BATCH53_HIRING_SLUGS,
  ...BATCH54_HIRING_SLUGS,
  ...BATCH55_HIRING_SLUGS,
  ...BATCH56_HIRING_SLUGS,
  ...BATCH57_HIRING_SLUGS,
  ...BATCH58_HIRING_SLUGS,
  ...BATCH59_HIRING_SLUGS,
  ...BATCH60_HIRING_SLUGS,
  ...BATCH61_HIRING_SLUGS,
  ...BATCH62_HIRING_SLUGS,
  ...BATCH63_HIRING_SLUGS,
  ...BATCH64_HIRING_SLUGS,
  ...BATCH65_HIRING_SLUGS,
  ...BATCH66_HIRING_SLUGS,
  ...BATCH67_HIRING_SLUGS,
  ...BATCH68_HIRING_SLUGS,
  ...BATCH69_HIRING_SLUGS,
  ...BATCH70_HIRING_SLUGS,
  ...BATCH71_HIRING_SLUGS,
  ...BATCH72_HIRING_SLUGS,
  ...BATCH73_HIRING_SLUGS,
  ...BATCH74_HIRING_SLUGS,
  ...BATCH75_HIRING_SLUGS,
]);

/** Sentinel heading for a given asset HTML (first <h2> text), for idempotency. */
function sentinelHeading(asset: string): string | null {
  const m = asset.match(/<h2[^>]*>(.*?)<\/h2>/i);
  return m ? m[1] : null;
}

/** Inserts an asset after the first </h2>, idempotently. */
function insertAfterFirstH2(content: string, asset: string): string {
  const sentinel = sentinelHeading(asset);
  if (sentinel && content.includes(sentinel)) return content;
  const m = content.match(/<\/h2>\s*/);
  if (!m) return content;
  const at = (m.index ?? 0) + m[0].length;
  return `${content.slice(0, at)}\n${asset}\n${content.slice(at)}`;
}

/** Inserts an asset before the second <h2>, idempotently (falls back after first). */
function insertBeforeSecondH2(content: string, asset: string): string {
  const sentinel = sentinelHeading(asset);
  if (sentinel && content.includes(sentinel)) return content;
  const h2Matches = [...content.matchAll(/<h2/gi)];
  if (h2Matches.length >= 2) {
    const at = h2Matches[1].index!;
    return `${content.slice(0, at)}\n${asset}\n\n${content.slice(at)}`;
  }
  return insertAfterFirstH2(content, asset);
}

/** Rewrites the first paragraph of a batch article to the answer-first lead. */
function applyAnswerFirstLead(
  content: string,
  slug: string,
  leads: Record<string, string>,
): string {
  const lead = leads[slug];
  if (!lead) return content;
  // Idempotency: if the full lead (links included) is already present, skip.
  // A 40-char prefix check would false-match a linkless Quick Answer paragraph
  // that shares the lead's opening text, silently dropping the links.
  if (content.includes(lead)) return content;
  // Match either "<h1>...</h1> <p>first</p>" or a bare leading "<p>first</p>"
  // (some posts have no H1 in their content body).
  const firstParaMatch = content.match(
    /(?:<h1[^>]*>[\s\S]*?<\/h1>\s*)?<p[^>]*>[\s\S]*?<\/p>/i,
  );
  if (!firstParaMatch) {
    // Fallback: content begins with a heading (e.g. <h2>) before any <p>.
    // Prepend the answer-first lead so the article opens with a direct answer.
    const headingMatch = content.match(/^[\s\S]*?<h[1-6]/i);
    if (headingMatch) {
      return `${lead}\n${content}`;
    }
    return content;
  }
  const matchText = firstParaMatch[0];
  const matchStart = firstParaMatch.index!;
  // Extract just the <p>...</p> portion to replace.
  const pOnly = matchText.match(/<p[^>]*>[\s\S]*?<\/p>/i)![0];
  const pStart = matchStart + (matchText.length - pOnly.length);
  return `${content.slice(0, pStart)}${lead}${content.slice(
    pStart + pOnly.length,
  )}`;
}

/** Deterministic FNV-1a hash for per-article template rotation. */
function hashSlug(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Matches the formulaic lead tail baked into Batches 34-51:
 *   "If you want to [svc], or you want [svc/home], or you want [home], closing.</p>"
 * Inter-link commas are optional (Batch 34 omits the first one); the separator
 * before the closing clause is always a comma.
 */
const LEAD_TAIL_RE =
  /If you want to (<a\b[^>]*>[\s\S]*?<\/a>),?\s*or you want (<a\b[^>]*>[\s\S]*?<\/a>)(?:,?\s*or you want (<a\b[^>]*>[\s\S]*?<\/a>))?,\s*([\s\S]*?)<\/p>/;

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Rewrites the formulaic "If you want to [A], or you want [B], or you want [C]"
 * tail baked into hundreds of answer-first leads (Batches 34-51). The home link
 * is lifted out of the first paragraph so applyHiringHomepageLink can place it
 * naturally in the second paragraph; the service link(s) are woven into one
 * comparative sentence; the closing punchline is preserved. A slug-keyed
 * rotation varies the skeleton across articles so the same formula never
 * repeats verbatim. Idempotent — no match after the first pass.
 */
function distributeStackedLeadLinks(content: string, slug: string): string {
  const h = hashSlug(slug);
  let occurrence = 0;
  // Replace EVERY stacked tail in the article (lead + any body copy), not just
  // the first — otherwise a second baked-in stack in the body survives.
  return content.replace(LEAD_TAIL_RE, (whole, ...args) => {
    const links = [
      args[0] as string,
      args[1] as string,
      args[2] as string,
    ].filter(Boolean) as string[];
    const closing = ((args[3] as string) ?? "").replace(/[.\s]+$/, "");
    const homeIdx = links.findIndex((l) => /href="\/"/.test(l));
    const serviceLinks =
      homeIdx >= 0 ? links.filter((_, i) => i !== homeIdx) : links;
    const capClause = capitalize(closing);
    const k = (h + occurrence) >>> 0;
    occurrence++;
    let sentence: string;
    if (serviceLinks.length >= 2) {
      const [a, b] = serviceLinks;
      const templates = [
        `Most owners weigh whether to ${a} for the build or ${b} for the architecture`,
        `The split is usually ${a} for now and ${b} when the system needs to scale`,
        `Before locking anything, teams compare ${a} and ${b} against the scope`,
        `The call usually comes down to ${a} for the immediate build or ${b} for the long-term system`,
      ];
      sentence = templates[k % templates.length];
    } else if (serviceLinks.length === 1) {
      const a = serviceLinks[0];
      const templates = [
        `Most owners resolve this by choosing to ${a} once the scope is clear`,
        `The move that settles it is to ${a} before the first workflow ships`,
        `That is why teams that plan to ${a} lock the scope first`,
        `Before any of that, the decision to ${a} is the one worth getting right`,
      ];
      sentence = templates[k % templates.length];
    } else {
      sentence = "";
    }
    const tail = sentence ? `${sentence}. ${capClause}.` : `${capClause}.`;
    return tail + "</p>";
  });
}

/** Migrates every CTA from the legacy /#contact anchor to the /book page. */
function migrateCTAs(content: string): string {
  return content
    .replace(/href="\/#contact"/g, 'href="/book"')
    .replace(/href="#contact"/g, 'href="/book"');
}

/** Removes the bolded exact-slug phrase wrapper in the first paragraph. */
function deStuffFirstParagraph(content: string, slug: string): string {
  const phrase = slug.replace(/-/g, " ");
  const firstP = content.match(/<p[^>]*>[\s\S]*?<\/p>/i);
  if (!firstP) return content;
  const re = new RegExp(
    `<strong>\\s*${phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*</strong>`,
    "i",
  );
  const destruffed = firstP[0].replace(re, (m) =>
    m.replace(/<\/?strong>/gi, ""),
  );
  return (
    content.slice(0, firstP.index!) +
    destruffed +
    content.slice(firstP.index! + firstP[0].length)
  );
}

/** Adds a single homepage contextual link into the 2nd paragraph (hiring topics). */
const HOME_LINK_ANCHORS = [
  "the vetting standards behind elite technical RevOps talent",
  "the direct-hire practice behind these systems",
  "how elite technical RevOps talent is actually vetted",
  "the architecture practice that ships these systems",
];

function applyHiringHomepageLink(content: string, slug: string): string {
  if (!ALL_HIRING_SLUGS.has(slug)) return content;
  if (content.includes('href="/" class="text-primary')) return content;
  const paras: { index: number; length: number; full: string }[] = [];
  const re = /<p[^>]*>[\s\S]*?<\/p>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    paras.push({ index: m.index, length: m[0].length, full: m[0] });
    if (paras.length >= 3) break;
  }
  if (paras.length < 2) return content;
  const second = paras[1];
  const anchor = HOME_LINK_ANCHORS[hashSlug(slug) % HOME_LINK_ANCHORS.length];
  const linkHtml = ` <a href="/" class="text-primary font-medium hover:underline">${anchor}</a>`;
  const updated = second.full.replace(/<\/p>\s*$/i, `${linkHtml}.</p>`);
  return (
    content.slice(0, second.index) +
    updated +
    content.slice(second.index + second.length)
  );
}

/**
 * Public entry point — runs all in-body optimizations for a post.
 * Non-batch posts are returned untouched (CTA migration still applies).
 */
import { BATCH52_SERVICE_LINKS } from "@/lib/batch52ServiceLinks";
import { BATCH53_SERVICE_LINKS } from "@/lib/batch53ServiceLinks";
import { BATCH54_SERVICE_LINKS } from "@/lib/batch54ServiceLinks";
import { BATCH55_SERVICE_LINKS } from "@/lib/batch55ServiceLinks";
import { BATCH56_SERVICE_LINKS } from "@/lib/batch56ServiceLinks";
import { BATCH57_SERVICE_LINKS } from "@/lib/batch57ServiceLinks";
import { BATCH58_SERVICE_LINKS } from "@/lib/batch58ServiceLinks";
import { BATCH59_SERVICE_LINKS } from "@/lib/batch59ServiceLinks";
import { BATCH60_SERVICE_LINKS } from "@/lib/batch60ServiceLinks";
import { BATCH61_SERVICE_LINKS } from "@/lib/batch61ServiceLinks";
import { BATCH62_SERVICE_LINKS } from "@/lib/batch62ServiceLinks";
import { BATCH63_SERVICE_LINKS } from "@/lib/batch63ServiceLinks";
import { BATCH64_SERVICE_LINKS } from "@/lib/batch64ServiceLinks";
import { BATCH65_SERVICE_LINKS } from "@/lib/batch65ServiceLinks";
import { BATCH66_SERVICE_LINKS } from "@/lib/batch66ServiceLinks";
import { BATCH67_SERVICE_LINKS } from "@/lib/batch67ServiceLinks";
import { BATCH68_SERVICE_LINKS } from "@/lib/batch68ServiceLinks";
import { BATCH69_SERVICE_LINKS } from "@/lib/batch69ServiceLinks";
import { BATCH70_SERVICE_LINKS } from "@/lib/batch70ServiceLinks";
import { BATCH71_SERVICE_LINKS } from "@/lib/batch71ServiceLinks";
import { BATCH72_SERVICE_LINKS } from "@/lib/batch72ServiceLinks";
import { BATCH73_SERVICE_LINKS } from "@/lib/batch73ServiceLinks";
import { BATCH74_SERVICE_LINKS } from "@/lib/batch74ServiceLinks";
import { BATCH75_SERVICE_LINKS } from "@/lib/batch75ServiceLinks";

/** Merged safety-net service-link map for batches whose leads embed the
 * service link inline but may lose it to the lead-swap idempotency guard
 * (Batch 52, 53, 54, 55). contextualLinks.ts cannot inline-import these
 * focused modules without exceeding its size ceiling. */
const BATCH_SAFETY_NET_LINKS = {
  ...BATCH52_SERVICE_LINKS,
  ...BATCH53_SERVICE_LINKS,
  ...BATCH54_SERVICE_LINKS,
  ...BATCH55_SERVICE_LINKS,
  ...BATCH56_SERVICE_LINKS,
  ...BATCH57_SERVICE_LINKS,
  ...BATCH58_SERVICE_LINKS,
  ...BATCH59_SERVICE_LINKS,
  ...BATCH60_SERVICE_LINKS,
  ...BATCH61_SERVICE_LINKS,
  ...BATCH62_SERVICE_LINKS,
  ...BATCH63_SERVICE_LINKS,
  ...BATCH64_SERVICE_LINKS,
  ...BATCH65_SERVICE_LINKS,
  ...BATCH66_SERVICE_LINKS,
  ...BATCH67_SERVICE_LINKS,
  ...BATCH68_SERVICE_LINKS,
  ...BATCH69_SERVICE_LINKS,
  ...BATCH70_SERVICE_LINKS,
  ...BATCH71_SERVICE_LINKS,
  ...BATCH72_SERVICE_LINKS,
  ...BATCH73_SERVICE_LINKS,
  ...BATCH74_SERVICE_LINKS,
  ...BATCH75_SERVICE_LINKS,
};

/** Wraps the first in-body occurrence of the anchor phrase in the
 * service-page link, idempotently. */
function injectBatchServiceLink(content: string, slug: string): string {
  const entry = BATCH_SAFETY_NET_LINKS[slug];
  if (!entry) return content;
  if (content.includes(`href="${entry.url}"`)) return content;
  const idx = content.toLowerCase().indexOf(entry.anchor.source.toLowerCase());
  if (idx === -1) return content;
  // Skip if already inside an anchor.
  const before = content.substring(0, idx);
  if (before.lastIndexOf("<a ") > before.lastIndexOf("</a>")) return content;
  const actualText = content.substring(idx, idx + entry.anchor.source.length);
  const replacement = `<a href="${entry.url}" class="text-primary hover:text-primary/80 underline decoration-primary/40 hover:decoration-primary transition-all font-medium" title="${entry.label}">${actualText}</a>`;
  return (
    content.substring(0, idx) +
    replacement +
    content.substring(idx + entry.anchor.source.length)
  );
}

export function optimizeBatchContent(content: string, slug: string): string {
  // CTA migration applies to all posts for consistency (idempotent).
  let out = migrateCTAs(content);

  // Data-driven batch dispatch (Batches 1, 4–37, sequential 23). Each entry
  // follows the identical pattern: answer-first lead, stacked-lead
  // de-clustering, keyword de-stuffing, hiring-topic homepage link, and a
  // unique info-gain asset inserted after the first H2. See batchDispatch.ts
  // for the ordered table.
  for (const { slugs, leads, infoGain } of BATCH_DISPATCH) {
    if (slugs.has(slug)) {
      out = applyAnswerFirstLead(out, slug, leads);
      out = distributeStackedLeadLinks(out, slug);
      out = deStuffFirstParagraph(out, slug);
      out = applyHiringHomepageLink(out, slug);
      const asset = infoGain[slug];
      if (asset) out = insertAfterFirstH2(out, asset);
      // Safety net (Batch 52–55): guarantee the /gohighlevel-revops-architect
      // service link renders even if the lead swap dropped it.
      if (BATCH_SAFETY_NET_LINKS[slug]) {
        out = injectBatchServiceLink(out, slug);
      }
      return out;
    }
  }

  // Batch 23 (original, non-sequential) — only the labeled information-gain
  // asset is added here (per guide §11), inserted before the second H2.
  if (BATCH23_SLUGS.has(slug)) {
    const asset = BATCH23_INFO_GAIN[slug];
    if (asset) out = insertBeforeSecondH2(out, asset);
    return out;
  }

  return out;
}
